/** Pure parse/merge rules for list view state in the URL (DESIGN.md §3.7). No React. */

export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export type SortOrder = 'asc' | 'desc';

/** A positive whole number, else 1. */
export const parsePage = (raw: string | null | undefined): number => {
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 ? n : 1;
};

/** One of `options`, else the default. */
export const parsePageSize = (
  raw: string | null | undefined,
  options: readonly number[] = PAGE_SIZE_OPTIONS,
  fallback = DEFAULT_PAGE_SIZE,
): number => {
  const n = Number(raw);
  return options.includes(n) ? n : fallback;
};

/** `raw` when it is one of `allowed`, else `fallback`. */
export const parseEnum = <T extends string>(
  raw: string | null | undefined,
  allowed: readonly T[],
  fallback: T,
): T => (allowed.includes(raw as T) ? (raw as T) : fallback);

/** `sort` must be one of `fields`; `order` is `asc` unless it says `desc`. No valid field means no sort. */
export const parseSort = <F extends string>(
  sort: string | null | undefined,
  order: string | null | undefined,
  fields: readonly F[],
): { sort: F; order: SortOrder } | null =>
  fields.includes(sort as F) ? { sort: sort as F, order: order === 'desc' ? 'desc' : 'asc' } : null;

/** The next sort after clicking `field`: a new field starts ascending, the same field flips. */
export const toggleSort = (
  current: { sort: string; order: SortOrder } | null,
  field: string,
): { sort: string; order: SortOrder } =>
  current?.sort === field
    ? { sort: field, order: current.order === 'asc' ? 'desc' : 'asc' }
    : { sort: field, order: 'asc' };

/** Page controls show whenever there is more than one page of rows (D-26). */
export const showPager = (totalRows: number, pageSize: number): boolean => totalRows > pageSize;

/** The last page that has rows (at least 1). */
export const pageCount = (totalRows: number, pageSize: number): number =>
  Math.max(1, Math.ceil(totalRows / pageSize));

/** `page` held inside 1..pageCount. */
export const clampPage = (page: number, totalRows: number, pageSize: number): number =>
  Math.min(Math.max(1, page), pageCount(totalRows, pageSize));

export interface ListPatch {
  q?: string;
  page?: number;
  pageSize?: number;
  sort?: string | null;
  order?: SortOrder | null;
  /** Filter values by URL key; empty means cleared. */
  filters?: Record<string, string | boolean | null | undefined>;
}

/**
 * The `useUrlParams().set` patch for a list change. Defaults and empty values are omitted so a
 * clean list has a clean URL, and any change to what is listed returns to page 1 unless a page
 * is named.
 */
export function patchList(
  patch: ListPatch,
  { defaultPageSize = DEFAULT_PAGE_SIZE }: { defaultPageSize?: number } = {},
): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  const { q, page, pageSize, sort, order, filters } = patch;

  if (q !== undefined) out.q = q === '' ? null : q;
  if (pageSize !== undefined) out.pageSize = pageSize === defaultPageSize ? null : String(pageSize);
  if (sort !== undefined) out.sort = sort || null;
  if (order !== undefined) out.order = order || null;
  for (const [key, value] of Object.entries(filters ?? {})) {
    out[key] =
      value === undefined || value === null || value === false || value === ''
        ? null
        : String(value);
  }
  if (page !== undefined) out.page = page <= 1 ? null : String(page);
  else out.page = null;
  return out;
}

/** A multi-value filter in one URL key: `a,b,c`, each value percent-encoded so a comma inside a value survives. */
export const joinValues = (values: readonly string[]): string =>
  values.map(encodeURIComponent).join(',');

const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value; // a hand-typed "100%" in the address bar stays as typed
  }
};

/** The values `joinValues` wrote; empty or missing means none. */
export const splitValues = (raw: string | null | undefined): string[] =>
  raw ? raw.split(',').filter(Boolean).map(safeDecode) : [];

/** The 1-based page that holds row `offset` of a server list paged by `offset` (old `?offset=` links), else 1. */
export const pageFromOffset = (raw: string | null | undefined, pageSize: number): number => {
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? Math.floor(n / pageSize) + 1 : 1;
};
